import { Container } from 'hypo-container';

import { BusinessUnitIdParser } from '@estee/elc-formatters';
import { HttpTransport } from '@estee/elc-http-transport';

import Cart from '~domain/entities/Cart/Cart';
import CartSidebar from '~domain/entities/CartSidebar/CartSidebar';
import Config from '~domain/entities/Config/Config';
import { ICartSidebarServiceConfig } from '~domain/entities/Config/IConfig';

import CartSidebarViewController from '~exported/CartSidebar/controller/CartSidebarViewController';
import { ICartSidebarViewController } from '~exported/CartSidebar/controller/ICartSidebarViewController';

import CartRepository from '~repositories/CartRepository/CartRepository';
import ConfigRepository from '~repositories/ConfigRepository/ConfigRepository';
import { ICartRepositoryProps } from '~repositories/CartRepository/ICartRepository';
import { CollectionRepository } from '~repositories/CollectionRepository/CollectionRepository';
import RecommendedItemsRepository from '~repositories/RecommendedRepository/RecommendedItemsRepository';

import DonationsController from '~views/DonationsExpander/DonationsController';
import RecommendedItemsViewController from '~views/RecommendedForYouModule/RecommendedItemsCarousel/RecommendedItemsViewController';
import ReplenishmentViewController from '~views/ShoppingBagTab/CartSidebarItems/CartItem/ReplenishmentComponent/ReplenishmentViewController';

import { serviceNames, CartService } from '~setup/CartService';
import ConfigStore from '~setup/ConfigStore';
import { SentryService } from '~setup/SentryService';
import ServiceBusController from '~setup/ServiceBusController';

import { CartServiceStartedUseCase } from '~use-cases/CartServiceStartedUseCase';

export const diContainer = new Container();

export const setConfig = (config: ICartSidebarServiceConfig) => {
    const configEntity: Config = diContainer.get(serviceNames.config);
    configEntity.updateConfig(config);
};

export async function serviceInitializer(config: ICartSidebarServiceConfig) {
    setConfig(config);

    const cartServiceStartedUseCase = new CartServiceStartedUseCase(['currency']);

    await cartServiceStartedUseCase.execute();
}

diContainer
    .register(serviceNames.sentryService, () => new SentryService())
    .register(serviceNames.collectionRepository, () => new CollectionRepository())
    .register(serviceNames.cartEntity, () => new Cart())
    .register(serviceNames.cartSidebar, () => new CartSidebar())
    .register(serviceNames.config, () => new Config())
    .register(serviceNames.configRepository, (c) => {
        const config = {
            entity: c.get(serviceNames.config)
        };

        return new ConfigRepository(config);
    })
    .register(serviceNames.configStore, (c) => new ConfigStore(c.config))
    .register(serviceNames.cartRepository, (c) => {
        const payload: ICartRepositoryProps = {
            cart: c.get(serviceNames.cartEntity),
            cartSidebar: c.get(serviceNames.cartSidebar)
        };

        return new CartRepository(payload);
    })
    .register(serviceNames.serviceBusController, () => new ServiceBusController())
    .register(serviceNames.httpTransport, () => new HttpTransport())
    .register(serviceNames.cartService, () => new CartService(serviceInitializer))
    .register(
        serviceNames.priceFormatter,
        diContainer.factory((c) => {
            const {
                config: { currency = 'USD', businessUnitId = '' }
            } = c.get(serviceNames.config);

            const { locale } = businessUnitId
                ? new BusinessUnitIdParser(businessUnitId)
                : { locale: 'en-US' };

            return new Intl.NumberFormat(locale, { currency, style: 'currency' });
        })
    )
    .register(serviceNames.cartSidebarViewController, (c) => {
        const payload: ICartSidebarViewController = {
            cartRepository: c.get(serviceNames.cartRepository),
            cartSidebar: c.get(serviceNames.cartSidebar)
        };

        return new CartSidebarViewController(payload);
    })
    .register(serviceNames.recommendedItemsRepository, () => new RecommendedItemsRepository())
    .register(
        serviceNames.recommendedItemsViewController,
        (c) =>
            new RecommendedItemsViewController({
                recommendedRepo: c.get(serviceNames.recommendedItemsRepository),
                cartRepo: c.get(serviceNames.cartRepository)
            })
    )
    .register(serviceNames.donationsViewController, () => new DonationsController())
    .register(serviceNames.replenishmentViewController, () => new ReplenishmentViewController());
